import { useEffect, useState } from 'react';

export type UseScriptStatus = 'loading' | 'ready' | 'error';
export type SCRIPT = HTMLScriptElement;

interface UseScriptOptions {
	position?: string;
	removeOnUnmount?: boolean;
	isReload?: boolean;
	initialLoad?: boolean;
}
export interface UseScript {
	type: string;
	src: string;
	fncls?: string;
	innerHTML?: string;
	option: UseScriptOptions;
}

function useExternalScript(props: UseScript[]): UseScriptStatus {
	const [status, setStatus] = useState<UseScriptStatus>('loading');

	const loadScript = (el: UseScript, parentNode: Element | null): SCRIPT | null => {
		const script: SCRIPT = document.createElement('script');
		const { option, ...newel } = el;
		// eslint-disable-next-line no-restricted-syntax
		for (const [key, value] of Object.entries(newel)) {
			if (key === 'innerHTML') {
				script.innerHTML = value;
			} else {
				script.setAttribute(key, value);
			}
		}
		script.setAttribute('data-status', el.src ? 'loading' : 'ready');
		parentNode?.appendChild(script);

		const setAttributeFromEvent = (event: Event) => {
			const scriptStatus: UseScriptStatus = event.type === 'load' ? 'ready' : 'error';

			script?.setAttribute('data-status', scriptStatus);
		};

		script.addEventListener('load', setAttributeFromEvent);
		script.addEventListener('error', setAttributeFromEvent);
		return script;
	};

	const setStateFromEvent = (event: Event) => {
		const newStatus = event.type === 'load' ? 'ready' : 'error';
		setStatus(newStatus);
	};

	useEffect(() => {
		const refArr: {
			script: SCRIPT | null;
			option: UseScriptOptions;
		}[] = [];
		props.forEach((el: UseScript) => {
			// check to load or not
			if (el.option.initialLoad) {
				setStatus('ready');
				return;
			}

			const {
				option: { position = 'body' }
			} = el;
			const parentNode = document.querySelector(position);

			let getscript: SCRIPT | null = document.querySelector(`script[src="${el.src}"]`);
			if (el?.option?.isReload && getscript) {
				getscript.remove();
				getscript = null;
			}
			if (!getscript) {
				const script: SCRIPT | null = loadScript(el, parentNode);
				// Add event listeners
				script?.addEventListener('load', setStateFromEvent);
				script?.addEventListener('error', setStateFromEvent);
				refArr.push({ script, option: el?.option });
			} else {
				setStatus((getscript?.getAttribute('data-status') as UseScriptStatus | undefined) ?? 'loading');
			}
		});

		// Remove event listeners on cleanup
		return () => {
			if (refArr.length > 0) {
				refArr.forEach(element => {
					if (element && element?.option.removeOnUnmount) {
						element.script?.removeEventListener('load', setStateFromEvent);
						element.script?.removeEventListener('error', setStateFromEvent);
						element.script?.remove();
					}
				});
			}
		};
	}, []); //eslint-disable-line

	return status;
}

export default useExternalScript;
